import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ShieldPlus, Trash2, Moon, Sun, AlertCircle } from 'lucide-react';
import { useDuties, useCreateDuty, useDeleteDuty, type DutyShutter } from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';

/**
 * LES GARDES — déclarées à la suite des horaires d'ouverture.
 *
 * C'est le même sujet : « quand l'officine est-elle ouverte ». Une garde est
 * une ouverture exceptionnelle, un jour donné, sur des heures à elle.
 *
 * Ici on déclare **la garde de l'officine**. Qui la prend se désigne depuis le
 * planning de la semaine, sous la grille — c'est là qu'on a les gens sous les
 * yeux.
 */

/** Durée d'une garde, passage de minuit compris. Doit dire la même chose que `services/duty.ts`. */
function dutyHours(start: string, end: string): number {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0;
  const s = sh + sm / 60;
  const e = eh + em / 60;
  return e > s ? e - s : e + 24 - s;
}

const fmtHours = (h: number) =>
  Number.isInteger(h) ? `${h} h` : `${Math.floor(h)} h ${Math.round((h % 1) * 60)}`;

export default function DutySection() {
  // On montre l'année en cours et la suivante : une garde se planifie des mois
  // à l'avance, mais personne ne remonte dans le passé pour en déclarer une.
  const range = useMemo(() => {
    const today = new Date();
    const from = format(new Date(today.getFullYear(), today.getMonth() - 1, 1), 'yyyy-MM-dd');
    const to = format(new Date(today.getFullYear() + 1, 11, 31), 'yyyy-MM-dd');
    return { from, to };
  }, []);

  const { data: duties, isLoading } = useDuties(range.from, range.to);
  const createMut = useCreateDuty();
  const deleteMut = useDeleteDuty();

  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState('');
  const [shutter, setShutter] = useState<DutyShutter>('CLOSED');
  const [startTime, setStartTime] = useState('19:30');
  const [endTime, setEndTime] = useState('08:30');
  const [error, setError] = useState<string | null>(null);

  const overnight = !!startTime && !!endTime && endTime <= startTime;
  const total = startTime && endTime ? dutyHours(startTime, endTime) : 0;

  function reset() {
    setAdding(false);
    setDate('');
    setShutter('CLOSED');
    setStartTime('19:30');
    setEndTime('08:30');
    setError(null);
  }

  async function save() {
    setError(null);
    if (!date) return setError('Choisissez une date.');
    if (!startTime || !endTime) return setError('Renseignez le début et la fin.');
    if (startTime === endTime) return setError('Le début et la fin ne peuvent pas être identiques.');
    try {
      await createMut.mutateAsync({ date, shutter, startTime, endTime });
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  }

  async function remove(id: string, label: string) {
    if (!window.confirm(`Supprimer la garde du ${label} ? Les personnes qui y étaient affectées perdront ces heures.`)) return;
    await deleteMut.mutateAsync(id);
  }

  return (
    <div className="mt-8">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Gardes</h2>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            Les jours où l'officine est de garde. Qui la prend se choisit dans le planning de la semaine.
          </div>
        </div>
        {!adding && (
          <Button variant="default" icon={ShieldPlus} onClick={() => setAdding(true)}>
            Ajouter une garde
          </Button>
        )}
      </div>

      {adding && (
        <div className="border border-primary/40 rounded-xl p-4 mb-4 bg-primary-bg/20">
          <div className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 items-center">
            <label className="text-[12px] font-medium text-slate-700">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input text-[12.5px] py-1.5 max-w-[190px]"
            />

            <label className="text-[12px] font-medium text-slate-700">Volet</label>
            <div className="flex gap-2">
              {([
                { v: 'OPEN' as const, label: 'Volet ouvert', icon: Sun, hint: "l'officine accueille comme d'habitude" },
                { v: 'CLOSED' as const, label: 'Volet fermé', icon: Moon, hint: 'service au guichet' },
              ]).map((opt) => {
                const Icon = opt.icon;
                const active = shutter === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setShutter(opt.v)}
                    title={opt.hint}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] border transition-colors ${
                      active
                        ? 'border-primary bg-primary text-white'
                        : 'border-slate-200 text-slate-600 hover:bg-white'
                    }`}
                  >
                    <Icon size={13} />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <label className="text-[12px] font-medium text-slate-700">Horaires</label>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="input text-[12.5px] py-1.5 tabular-nums max-w-[110px]"
              />
              <span className="text-[11px] text-slate-400">→</span>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="input text-[12.5px] py-1.5 tabular-nums max-w-[110px]"
              />
              {total > 0 && (
                <span className="text-[11.5px] text-slate-600 ml-1">
                  {fmtHours(total)}
                  {/* Dit explicitement que la garde finit le lendemain : c'est la
                      seule chose que la paire d'heures ne montre pas d'elle-même. */}
                  {overnight && <span className="text-slate-500"> — se termine le lendemain</span>}
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-3 flex items-start gap-1.5 text-[12px] text-danger">
              <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <Button variant="primary" size="sm" loading={createMut.isPending} onClick={save}>
              Enregistrer la garde
            </Button>
            <Button variant="ghost" size="sm" onClick={reset}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-[12px] text-slate-500">Chargement…</div>
      ) : !duties || duties.length === 0 ? (
        <div className="border border-slate-200 rounded-xl p-6 text-center text-[12.5px] text-slate-500">
          Aucune garde déclarée.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
          {duties.map((d) => {
            const day = parseISO(d.date);
            const label = format(day, 'EEEE d MMMM yyyy', { locale: fr });
            return (
              <div key={d.id} className="px-4 py-2.5 flex items-center gap-3">
                <div className="w-[210px] text-[12.5px] font-medium text-slate-800 capitalize">
                  {label}
                </div>
                <div className="text-[12px] text-slate-600 tabular-nums w-[150px]">
                  {d.startTime} → {d.endTime}
                  {d.overnight && <span className="text-slate-400"> (+1 j)</span>}
                </div>
                <div className="text-[12px] text-slate-500 w-[70px] tabular-nums">{fmtHours(d.hours)}</div>
                <Chip variant={d.shutter === 'OPEN' ? 'primary' : 'slate'} size="sm">
                  {d.shutter === 'OPEN' ? 'Volet ouvert' : 'Volet fermé'}
                </Chip>
                <div className="flex-1 text-[11.5px] text-slate-500 truncate">
                  {d.assignments.length === 0
                    ? 'Personne de désigné'
                    : d.assignments
                        .map((a) => `${a.employee.firstName} ${a.employee.lastName[0]}.`)
                        .join(', ')}
                </div>
                <button
                  onClick={() => remove(d.id, label)}
                  className="p-1.5 rounded hover:bg-danger-bg text-slate-400 hover:text-danger"
                  title="Supprimer cette garde"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
